import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { getShowDebtorDocuments } from '@/api-sdk';
import { DownloadsMixin } from '@/components/downloads/downloads-mixin';
import { iconCircleInfo } from '@/components/icons';
import { DownloadItem, DownloadStatus } from '@/types';

import './fg-downloads';

@customElement('fg-reports-downloads')
@localized()
export class FgReportsDownloads extends DownloadsMixin(LitElement) {
  @state()
  reportListDownloads: DownloadItem[] = [];

  @state()
  isLoading: boolean = true;

  async firstUpdated() {
    await this.downloadReports();
  }

  async openLink(link: string) {
    window.location.href = link;
  }

  async downloadReports() {
    try {
      const reports = await getShowDebtorDocuments({
        debtorCode: this.debtor,
        params: {
          locale: this.locale,
        },
      });

      this.reportListDownloads = reports.map((report, index) => ({
        name: report.name,
        url: report.url,
        id: `${report.name?.toLowerCase()}-${index}`,
        type: this.getFileType(report.url),
      }));

      this.reportListDownloads.forEach(download =>
        this.updateDownloadStatus(download.id, DownloadStatus.INITIAL),
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  async downloadReport(event: CustomEvent) {
    const { download } = event.detail;
    await this.openLink(download.url);
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="loading-container">
          <fg-loader .type=${'in-component'}></fg-loader>
        </div>
      `;
    }

    if (this.reportListDownloads.length === 0) {
      return html`
        <div class="no-downloads-note">
          <span class="no-downloads-icon">${iconCircleInfo}</span>
          <span>${t('labels.noDocuments')}</span>
        </div>
      `;
    }

    return html`
      <fg-downloads
        .downloadStatus=${this.downloadStatus}
        .downloads=${this.reportListDownloads}
        @download-file=${this.downloadReport}
      ></fg-downloads>
    `;
  }

  static styles = [
    css`
      .loading-container {
        position: relative;
        height: 200px;
      }

      .no-downloads-note {
        padding: 12px 16px;
        background-color: var(
          --fg-infobox-background,
          var(--fg-fallback-infobox-background)
        );
        color: var(--fg-infobox-content, var(--fg-fallback-infobox-content));
        border: 1px solid
          var(--fg-infobox-content, var(--fg-fallback-infobox-content));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        display: flex;
        gap: 4px;
        margin: 8px 0;
      }

      .no-downloads-icon {
        display: flex;
        align-items: center;
      }

      .no-downloads-note svg {
        width: 16px;
        height: 16px;
        fill: var(--fg-infobox-content, var(--fg-fallback-infobox-content));
      }
    `,
  ];
}
